import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Minimal History-API router.
 *
 * This app has four static routes, no route parameters, no nested layouts and no
 * data loaders — the entire feature surface of react-router that we would use is
 * "render a component for a path". Every published react-router version
 * currently carries an open high-severity advisory, so the dependency was
 * replaced with the ~60 lines below rather than shipping a known-vulnerable
 * package or documenting an exception.
 *
 * Safety note: `navigate` only accepts app-internal paths and rejects anything
 * that could be reinterpreted as an absolute or protocol-relative URL, which is
 * the class of open-redirect bug that affected the library it replaces.
 */

function currentPath(): string {
  return window.location.pathname || '/';
}

/** Rejects `//evil.com`, `https://…`, `javascript:` and similar. */
function isSafeInternalPath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//') && !/^\/\\/.test(path);
}

export function navigate(path: string, options: { replace?: boolean } = {}): void {
  if (!isSafeInternalPath(path)) {
    throw new Error(`Refusing to navigate to a non-internal path: ${path}`);
  }
  if (path === currentPath()) return;

  window.history[options.replace ? 'replaceState' : 'pushState']({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function usePathname(): string {
  const [pathname, setPathname] = useState(currentPath);

  useEffect(() => {
    const onChange = () => setPathname(currentPath());
    window.addEventListener('popstate', onChange);
    return () => window.removeEventListener('popstate', onChange);
  }, []);

  return pathname;
}

export interface RouteDefinition {
  path: string;
  element: ReactNode;
}

/**
 * Renders the first exact match, falling back to `fallback`.
 *
 * Exact matching is all this app needs; `/adverts` and `/adverts/new` are
 * siblings rather than parent and child.
 */
export function Routes({ routes, fallback }: { routes: RouteDefinition[]; fallback: string }) {
  const pathname = usePathname();
  const match = useMemo(() => routes.find((route) => route.path === pathname), [routes, pathname]);

  useEffect(() => {
    if (!match) navigate(fallback, { replace: true });
  }, [match, fallback]);

  return <>{match?.element ?? null}</>;
}

export function Link({
  to,
  className,
  activeClassName,
  inactiveClassName,
  children,
}: {
  to: string;
  className?: string;
  activeClassName?: string;
  inactiveClassName?: string;
  children: ReactNode;
}) {
  const pathname = usePathname();
  const isActive = pathname === to;

  const onClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      // Let the browser handle modified clicks (new tab, download, …).
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      navigate(to);
    },
    [to],
  );

  return (
    <a
      href={to}
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      className={clsx(className, isActive ? activeClassName : inactiveClassName)}
    >
      {children}
    </a>
  );
}
